import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css'
import {sdk} from './sdk.js';
import {sdkLocal} from './sdkLocal.js';
import './NewSocialPostForm.css';
import { SocialUserAvatar } from "./SocialUserAvatar";
import { SocialPageAvatar } from "./SocialPageAvatar";
import { FaTimes } from 'react-icons/fa';
import DestinationSelector from './DestinationSelector';
import ScheduleSection from './ScheduleSection';
import FeelingTag from './FeelingTag';
import LinkPreview from './LinkPreview';
import AttachmentsDisplay from './AttachmentsDisplay';
import FeelingSelector from './FeelingSelector';
import PostFooter from './PostFooter';
import SharedPostDisplay from './SharedPostDisplay';

const NewSocialPostForm = ({ 
  user, 
  page, 
  group, 
  setNewPost, 
  toggleModal, 
  sharedFromId, 
  isOpen,
  editMode = false,
  postToEdit = null
}) => {
  const [content, setContent] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [postDestination, setPostDestination] = useState("wall");
  const [userGroups, setUserGroups] = useState([]);
  const [userPages, setUserPages] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [linkPreview, setLinkPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [showFeelingSelector, setShowFeelingSelector] = useState(false);
  const [feeling, setFeeling] = useState(null);
  const [isScheduledPost, setIsScheduledPost] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  
  const textareaRef = useRef(null);
  const previousContentRef = useRef("");

  // Fonction pour obtenir la date et heure actuelles au format input
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    
    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}`
    };
  };

  // Auto-resize du textarea
  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    autoResize();
  }, [content]);

  // URL detection logic - VERSION MODIFIÉE pour conserver le preview
  useEffect(() => {
    // En mode édition, on ne fait pas la détection d'URL automatique
    // pour ne pas remplacer l'aperçu existant
    if (editMode) return;
    
    const detectAndProcessUrl = async () => {
      // URL regex pattern
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const match = content.match(urlRegex);
      
      // Ne traiter que s'il y a une URL et qu'on n'a pas encore de preview
      if (match && !linkPreview && !isLoadingPreview) {
        const url = match[0];
        
        // Vérifier si cette URL est différente de celle déjà traitée
        if (url !== previousContentRef.current) {
          previousContentRef.current = url;
          
          setIsLoadingPreview(true);
          
          try {
            // Call the crawler
            const ogData = await sdk.models.Crawler.doUrl({ url });
            
            if (ogData) {
              setLinkPreview({
                url: ogData["og:url"] || url,
                title: ogData["og:title"] || "",
                image: ogData["og:image"] || "",
                description: ogData["og:description"] || "",
                type: ogData["og:type"] || ""
              });
            }
          } catch (error) {
            console.error("Error fetching link preview:", error);
          } finally {
            setIsLoadingPreview(false);
          }
        }
      }
      // SUPPRESSION DE LA PARTIE QUI SUPPRIMAIT LE PREVIEW
      // Maintenant le preview reste même si l'URL est supprimée du texte
    };

    // Utiliser un timeout pour éviter trop d'appels lors de la frappe rapide
    const timeoutId = setTimeout(detectAndProcessUrl, 300);
    
    return () => clearTimeout(timeoutId);
  }, [content, linkPreview, isLoadingPreview, editMode]);

  // Chargement des données initiales
  useEffect(() => {
    if (isOpen && user) {
      loadUserData();
      
      // En mode édition, pré-remplir les données
      if (editMode && postToEdit) {
        setContent(postToEdit.postText || "");
        
        // Charger les attachments existants
        if (postToEdit.postFile) {
          setAttachments([{
            filename: postToEdit.postFileName,
            convertedMaster: postToEdit.postFile,
            mediaThumb: postToEdit.postFileThumb,
            isExisting: true
          }]);
        }
        
        // Charger le feeling existant
        if (postToEdit.postFeeling) {
          try {
            const existingFeeling = JSON.parse(postToEdit.postFeeling);
            setFeeling(existingFeeling);
          } catch (e) {
            console.log("Erreur lors du parsing du feeling:", e);
          }
        }
        
        // Charger le link preview existant
        if (postToEdit.postLink) {
          setLinkPreview({
            url: postToEdit.postLink,
            title: postToEdit.postLinkTitle,
            description: postToEdit.postLinkContent,
            image: postToEdit.postLinkImage
          });
        }
      } else {
        // Réinitialiser pour un nouveau post
        resetForm();
        
        // Initialiser la date/heure par défaut pour les posts programmés
        const currentDateTime = getCurrentDateTime();
        setScheduledDate(currentDateTime.date);
        setScheduledTime(currentDateTime.time);
      }
    }
  }, [isOpen, editMode, postToEdit]);

  const resetForm = () => {
    setContent("");
    setAttachments([]);
    setLinkPreview(null);
    setFeeling(null);
    setIsScheduledPost(false);
    setPostDestination("wall");
    setSelectedGroup(null);
    setSelectedPage(null);
    previousContentRef.current = "";
    
    const currentDateTime = getCurrentDateTime();
    setScheduledDate(currentDateTime.date);
    setScheduledTime(currentDateTime.time);
  };

  const loadUserData = async () => {
    try {
      // Charger les groupes où l'utilisateur est membre
      const memberGroups = await sdk.models.SocialGroup.ImMembersOf({});
      setUserGroups(memberGroups || []);
      
      // Charger les pages de l'utilisateur
      const pages = await sdk.models.SocialPages.myPages({});
      setUserPages(pages || []);
    } catch (error) {
      console.error("Erreur lors du chargement des données utilisateur:", error);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file || editMode) return;

    handleFileUpload(file);
  };

  const handleFileUpload = async (file) => {
    if (editMode) return;
    
    const apiUrl = process.env.NODE_ENV === "development"
      ? "https://devapi.liberium.net/models/UsrMedia/uploadFile"
      : "https://api.liberium.net/models/UsrMedia/uploadFile";

    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem("liberium-token");
    
    setUploading(true);
    setIsProcessing(true);

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        body: formData,
        headers: { "liberium-token": token },
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setAttachments([data]);
        setIsProcessing(data.isProcessing);
      } else {
        console.error("Erreur lors du téléchargement", data);
      }
    } catch (error) {
      console.error("Erreur lors du téléchargement de l'image:", error);
    } finally {
      setUploading(false);
    }
  };

  const checkProcessingStatus = async () => {
    if (attachments.length === 0) return;
    const media = attachments[0];
    if (!media.isProcessing) {
      setIsProcessing(false);
      return;
    }
    try {
      const data = await sdk.models.UsrMedia.getOne(media.id);
      if (data.isProcessing === 0) {
        setAttachments([data]);
        setIsProcessing(false);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du statut du traitement:", error);
    }
  };

  useEffect(() => {
    let intervalId;
    if (isProcessing) {
      intervalId = setInterval(checkProcessingStatus, 1000);
    }
    return () => clearInterval(intervalId);
  }, [isProcessing]);

  // Validation de la date/heure programmée
  const validateScheduledDateTime = () => {
    if (!isScheduledPost) return true;
    
    if (!scheduledDate || !scheduledTime) {
      alert("Veuillez sélectionner une date et une heure pour le post programmé.");
      return false;
    }
    
    const selectedDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
    const now = new Date();
    
    if (selectedDateTime <= now) {
      alert("La date et l'heure sélectionnées doivent être dans le futur.");
      return false;
    }
    
    return true;
  };

  const handlePostSubmit = async () => {
    // Validation : le contenu est obligatoire seulement s'il n'y a pas d'attachments, de link preview ou de post partagé
    const hasContent = content.trim();
    const hasAttachments = attachments.length > 0;
    const hasLinkPreview = linkPreview !== null;
    const hasSharedPost = !!sharedFromId;
    
    if (!hasContent && !hasAttachments && !hasLinkPreview && !hasSharedPost) {
      return; // Rien à publier
    }
    
    if (isProcessing || isLoadingPreview) return;
    
    // Validation pour les posts programmés
    if (!validateScheduledDateTime()) return;
    
    setIsProcessing(true);
    
    try {
      // Mode édition
      if (editMode && postToEdit) {
        const updatedPost = {
          ...postToEdit,
          postText: content,
          postFeeling: feeling ? JSON.stringify(feeling) : null,
        };
        
        let response;
        
        if (updatedPost.page_id) {
          response = await sdk.models.post.update(updatedPost.id, updatedPost);
        } else if (updatedPost.group_id) {
          response = await sdk.models.post.update(updatedPost.id, updatedPost);
        } else {
          response = await sdk.models.post.update(updatedPost.id, updatedPost);
        }
        
        if (response) {
          if (typeof setNewPost === 'function') {
            setNewPost(response);
          }
          toggleModal();
        }
        return;
      }
      
      // Création de nouveau post
      const newPost = {
        postText: content,
        postFile: attachments[0]?.convertedMaster || attachments[0]?.filename,
        postFileName: attachments[0]?.filename,
        postFileThumb: attachments[0]?.mediaThumb,
        postFeeling: feeling ? JSON.stringify(feeling) : null,
        time: isScheduledPost 
          ? Math.floor(new Date(`${scheduledDate}T${scheduledTime}`).getTime() / 1000)
          : Math.floor(Date.now() / 1000),
      };

      // Ajouter les données de link preview
      if (linkPreview) {
        newPost.postLink = linkPreview.url;
        newPost.postLinkTitle = linkPreview.title;
        newPost.postLinkImage = linkPreview.image;
        newPost.postLinkContent = linkPreview.description;
        
        if (linkPreview.url.includes("youtube.com") || linkPreview.url.includes("youtu.be")) {
          newPost.postYoutube = linkPreview.url;
        }
      }

      if (typeof sharedFromId !== "undefined") newPost.shared_from = sharedFromId;
      
      let post;
      
      // Logique pour les posts de page avec programmation
      if ((postDestination === "page" && selectedPage) || page) {
        const pageId = page ? page.page_id : selectedPage;
        newPost.page_id = pageId;
        
        if (isScheduledPost) {
          // Utiliser PostsWaiting pour les posts programmés de page
          post = await sdk.models.PostsWaiting.create(newPost);
        } else {
          // Post immédiat de page
          post = await sdk.models.post.createPagePost(newPost);
        }
      } 
      // Logique pour les groupes (pas de programmation pour les groupes)
      else if (postDestination === "group" && selectedGroup) {
        newPost.user_id = user.user_id;
        newPost.group_id = selectedGroup;
        post = await sdk.models.post.createGroupPost(newPost);
      } else if (group) {
        newPost.user_id = user.user_id;
        newPost.group_id = group.id;
        post = await sdk.models.post.createGroupPost(newPost);
      } 
      // Posts d'utilisateur normal (pas de programmation)
      else {
        newPost.user_id = user.user_id;
        post = await sdk.models.post.create(newPost);
      }
      
      if (post) {
        setNewPost?.(post);
        resetForm();
        toggleModal();
      }
    } catch (error) {
      console.error("Erreur lors de la création du post:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const removeAttachment = () => {
    if (editMode) return;
    setAttachments([]);
    setIsProcessing(false);
  };

  const removeLinkPreview = () => {
    setLinkPreview(null);
    previousContentRef.current = "";
  };

  const removeFeeling = () => {
    setFeeling(null);
  };

  const posterAvatar = page ? (
    <SocialPageAvatar page_id={page.page_id} />
  ) : (
    <SocialUserAvatar user_id={user.user_id} />
  );

  const canSchedule = page || (postDestination === "page" && selectedPage);
  const canSubmit = content.trim() || attachments.length > 0 || linkPreview !== null || !!sharedFromId;

  if (!isOpen) return null;

  return (
    <div className="npf-overlay">
      <div className="npf-container">
        <div className="npf-header">
          <div className="npf-header-title">
            {posterAvatar}
            <h5>{editMode ? "Modifier la publication" : "Créer une publication"}</h5>
          </div>
          <div className="npf-close-icon" onClick={toggleModal}>
            <FaTimes />
          </div>
        </div>
        
        <div className="npf-body">
          {/* Sélecteur de destination */}
          {!editMode && !page && !group && (userGroups.length > 0 || userPages.length > 0) && (
            <DestinationSelector
              postDestination={postDestination}
              setPostDestination={setPostDestination}
              userGroups={userGroups}
              userPages={userPages}
              selectedGroup={selectedGroup}
              setSelectedGroup={setSelectedGroup}
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
              setIsScheduledPost={setIsScheduledPost}
            />
          )}

          {/* Section de programmation */}
          {!editMode && canSchedule && (
            <ScheduleSection
              isScheduledPost={isScheduledPost}
              setIsScheduledPost={setIsScheduledPost}
              scheduledDate={scheduledDate}
              setScheduledDate={setScheduledDate}
              scheduledTime={scheduledTime}
              setScheduledTime={setScheduledTime}
              getCurrentDateTime={getCurrentDateTime}
            />
          )}
          
          <div className="npf-input-area">
            <FeelingTag feeling={feeling} onRemove={removeFeeling} />
            
            <textarea
              ref={textareaRef}
              className="npf-content-textarea"
              placeholder={sharedFromId ? `Dites quelque chose à propos de ce post... (optionnel)` : `Quoi de neuf, ${user?.first_name || 'vous'}?`}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={1}
            />
          </div>
          
          <LinkPreview
            isLoadingPreview={isLoadingPreview}
            linkPreview={linkPreview}
            onRemovePreview={removeLinkPreview}
          />
          
          <AttachmentsDisplay
            attachments={attachments}
            uploading={uploading}
            editMode={editMode}
            onRemoveAttachment={removeAttachment}
          />
          
          <SharedPostDisplay sharedFromId={sharedFromId} />
          
          <FeelingSelector
            isVisible={showFeelingSelector}
            onClose={() => setShowFeelingSelector(false)}
            onSelectFeeling={setFeeling}
          />
        </div>
        
        <PostFooter
          editMode={editMode}
          isProcessing={isProcessing}
          isLoadingPreview={isLoadingPreview}
          canSubmit={canSubmit}
          isScheduledPost={isScheduledPost}
          onFileChange={handleFileChange}
          onToggleFeelingSelector={() => setShowFeelingSelector(!showFeelingSelector)}
          onSubmit={handlePostSubmit}
        />
      </div>
    </div>
  );
};

export { NewSocialPostForm };
export default NewSocialPostForm;